import React from "react";
import Highcharts from "highcharts";

import { Box, useTheme } from "@material-ui/core";

import Treemap from "components/visuals/Treemap";
import useColourPalette from "components/visuals/useColourPalette";

import {
    selectRetailComplexion,
    selectHasErrors,
    selectIsLoading,
    RetailComplexionLevel
} from "modules/customer/insights/portfolioNew/areaHealthNew/areaHealthNewSlice";
import { useAppSelector } from "store";
import numberFormatter from "utils/numberFormatter";
import stringUtils from "utils/stringUtils";

const RetailComplexion: React.FC = () => {
    const theme = useTheme();
    const colourPalette = useColourPalette();
    const isLoading = useAppSelector(selectIsLoading);
    const hasErrors = useAppSelector(selectHasErrors);
    const data = useAppSelector(selectRetailComplexion);
    const colours = [theme.palette.text.disabled, ...colourPalette.categorical];

    const chartData = data.map(row => {
        let colour = undefined;
        if (row.type === RetailComplexionLevel.BrandClassification) {
            colour = colours[row.colourId];
        }
        return {
            id: row.name,
            name: row.name,
            parent: row.parent,
            value: row.percentageOfLevel,
            color: colour,
            custom: {
                numberOfStores: row.numberOfStores,
                percentageOfLevel: row.percentageOfLevel,
                type: row.type,
                parentName: row.parent
            }
        };
    });

    const options = React.useMemo((): Highcharts.Options => ({
        chart: {
            height: 300,
            inverted: true
        },
        tooltip: {
            useHTML: true,
            headerFormat: "",
            footerFormat: "",
            formatter: function (this: Highcharts.TooltipFormatterContextObject) {
                const customOptions = this.point?.options?.custom;
                const percentageTitle = customOptions?.type === RetailComplexionLevel.BrandClassification
                    ? "Percentage of total"
                    : `Percentage of ${customOptions?.parentName}`;
                return `<table><tr><td colspan="2" style="">${customOptions?.type}:</td></tr>
                ${stringUtils.tooltipHTML([" ", "Number of stores", percentageTitle], {
                    header: `${this.key}`,
                    values: [" ",
                        numberFormatter.toTruncatedInteger(customOptions?.numberOfStores, 1),
                        numberFormatter.toPercentage(this.point.value ?? 0, true, 1)
                    ],
                    valueFormatting: `color:${this.color}`
                }
                )}</table>`;
            }
        },
        series: [{
            type: "treemap",
            data: chartData,
            layoutAlgorithm: "stripes",
            layoutStartingDirection: "horizontal",
            levels: [{
                dataLabels: {
                    style: {
                        textOverflow: "clip",
                        fontFamily: theme.typography.fontFamily
                    },
                    crop: true,
                    overflow: "justify",
                    formatter: function () {
                        const pointPercentage = this.point.value ?? 0;
                        let formattedPointName = this.point.name;
                        if (formattedPointName.indexOf(" ") > -1) {
                            formattedPointName = this.point.name.replaceAll(" ", "<br>");
                        }

                        if (pointPercentage >= 10) {
                            return `<table>${stringUtils.tooltipHTML([`${numberFormatter.toPercentage(pointPercentage, true, 1)}`], {
                                header: formattedPointName,
                                values: ["&nbsp;"],
                                headerFormatting: `color: ${theme.palette.common.black}; fontWeight: 400; fontSize: 14px`,
                                categoryFormatting: `color: ${theme.palette.common.black}; fontWeight: 600; fontSize: 18px`,
                                overrideDefaultHeaderFormatting: true,
                                overrideDefaultCategoryFormatting: true
                            })}</table>`;
                        } else {
                            return "";
                        }

                    }
                }
            }]
        }]
    }), [chartData, theme]);

    return (
        <Box data-cy="customer-profiles-treemap">
            <Treemap loading={isLoading} error={hasErrors} options={options} />
        </Box>
    );
};

export default RetailComplexion;
